import type { ActivationAlertsMetricsSocialMediaEngagementDetailData } from "@similarweb/pro-api-client";

import type { ValueMap } from "../../common/widget/table/computeLeaders";
import { addToValueMap, getLeadersFromMap } from "../../common/widget/table/computeLeaders";
import type { MetricKey, SocialMediaEngagementApiItem, SocialMediaEngagementRow } from "../metrics/engagement/types";
import { METRIC_KEYS } from "../metrics/engagement/types";

type LeaderKey = MetricKey | "engagementScore";

const VALUE_EXTRACTORS: Record<LeaderKey, (item: SocialMediaEngagementApiItem) => number> = {
    views: (item) => item.views,
    comments: (item) => item.comments,
    reactions: (item) => item.reactions,
    shares: (item) => item.shares,
    engagementScore: (item) => item.engagementScore.score,
};

const LEADER_KEYS: readonly LeaderKey[] = [...METRIC_KEYS, "engagementScore"];

const computeLeaders = (data: readonly SocialMediaEngagementApiItem[]): Record<LeaderKey, Set<string>> => {
    const maps = Object.fromEntries(LEADER_KEYS.map((key) => [key, new Map() as ValueMap])) as Record<
        LeaderKey,
        ValueMap
    >;

    data.forEach((item) => {
        for (const key of LEADER_KEYS) {
            addToValueMap({ map: maps[key], value: VALUE_EXTRACTORS[key](item), domain: item.domain });
        }
    });

    return Object.fromEntries(LEADER_KEYS.map((key) => [key, getLeadersFromMap(maps[key])])) as Record<
        LeaderKey,
        Set<string>
    >;
};

export const transformSocialMediaEngagementData = ({
    data,
    icons,
    primaryDomain,
}: {
    data: ActivationAlertsMetricsSocialMediaEngagementDetailData;
    icons: Record<string, string> | undefined;
    primaryDomain?: string;
}): SocialMediaEngagementRow[] => {
    const leaders = computeLeaders(data);

    return data.map((row) => {
        const metricFields = Object.fromEntries(
            METRIC_KEYS.map((key) => [
                key,
                { value: row[key], isLeader: leaders[key].has(row.domain) },
            ]),
        ) as Pick<SocialMediaEngagementRow, MetricKey>;

        return {
            domain: {
                icon: icons?.[row.domain],
                isPrimary: row.domain === primaryDomain,
                value: row.domain,
            },
            ...metricFields,
            engagementScore: {
                score: row.engagementScore.score,
                change: row.engagementScore.change,
                isLeader: leaders.engagementScore.has(row.domain),
            },
        };
    });
};
